/**
 * Canonical enums for the Vari Crew Scheduler.
 *
 * Adding or renaming values in this file is a breaking change. See ADR-0010
 * for the change procedure (ADR + migration + every UI touched).
 */
import { z } from 'zod';
// ----------------------------------------------------------------------------
// Roles (assigned in-app; Entra is authentication only — see ADR-0015)
// ----------------------------------------------------------------------------
export const RoleSchema = z.enum(['crew', 'supervisor', 'pm', 'sales', 'admin']);
// ----------------------------------------------------------------------------
// User access lifecycle (DB-based RBAC — ADR-0015). Self-registrants land
// 'pending'; a supervisor/admin approves them into an active role.
// ----------------------------------------------------------------------------
export const UserStatusSchema = z.enum(['pending', 'active', 'denied']);
// ----------------------------------------------------------------------------
// Project status pipeline (linear advance + one alarm state)
// See ADR-0010
// ----------------------------------------------------------------------------
export const ProjectStatusSchema = z.enum([
    'requested',
    'scheduled',
    'en_route',
    'on_site',
    'punch',
    'pending_closeout',
    'complete',
    'blocked',
    // Terminal: the supervisor called the project off. Like `complete`/`blocked`
    // it sits outside the linear pipeline and is excluded from auto-rollup.
    'cancelled',
]);
/** The advance order of the linear status pipeline (excludes `blocked`). */
export const PROJECT_STATUS_PIPELINE = [
    'requested',
    'scheduled',
    'en_route',
    'on_site',
    'punch',
    'pending_closeout',
    'complete',
];
/** Status colors as semantic Tailwind tokens. UI components reference these. */
export const PROJECT_STATUS_TOKENS = {
    requested: 'status-requested',
    scheduled: 'status-scheduled',
    en_route: 'status-enroute',
    on_site: 'status-onsite',
    punch: 'status-punch',
    pending_closeout: 'status-pendingcloseout',
    complete: 'status-complete',
    blocked: 'status-blocked',
    cancelled: 'status-cancelled',
};
/** Human-friendly labels for UI. */
export const PROJECT_STATUS_LABELS = {
    requested: 'Requested',
    scheduled: 'Scheduled',
    en_route: 'En route',
    on_site: 'On site',
    punch: 'Punch / walkthrough',
    pending_closeout: 'Pending closeout',
    complete: 'Complete',
    blocked: 'Blocked',
    cancelled: 'Cancelled',
};
// ----------------------------------------------------------------------------
// Task types — first-class enum, never free text
// ----------------------------------------------------------------------------
// 'walkthrough' was removed (demo #9, Leo — not their term; the final
// inspection lives in the punch flow + the project recap). Legacy rows are
// migrated to 'punch'.
export const TaskTypeSchema = z.enum([
    'pickup',
    'install',
    'punch',
    'labor',
]);
export const PickupSubtypeSchema = z.enum(['warehouse', 'supplier', 'rma']);
// ----------------------------------------------------------------------------
// Assignment block anchor (D-3, FMS feedback 2026-08-11)
// ----------------------------------------------------------------------------
// The assignment block (`task_assignments`, ADR-0013: the scheduled truth) is
// the schedulable unit. It anchors to ONE of: a task (the original model), a
// project directly (crew assigned to the project, no task needed), or a pickup
// (a warehouse run that spans N projects' packing lists). Every scheduling
// surface reads blocks and branches on this, instead of each anchor type
// getting its own assignment table.
export const AssignmentAnchorKindSchema = z.enum(['task', 'project', 'pickup']);
/**
 * Pickup status (workstream C). A pickup is a lightweight warehouse run: it's
 * `scheduled` once it has a window, `in_progress` when the crew starts the run,
 * `complete` when they've finished (its projects then read as picked up), or
 * `cancelled`. Kept deliberately smaller than project status — a pickup has no
 * approval/bid/punch lifecycle.
 */
export const PickupStatusSchema = z.enum([
    'scheduled',
    'in_progress',
    'complete',
    'cancelled',
]);
export const InstallSubtypeSchema = z.enum(['new', 'reconfigure']);
/** Task-level status. Mirrors project status but task-scoped. */
export const TaskStatusSchema = z.enum([
    'scheduled',
    'en_route',
    'on_site',
    'in_progress',
    'paused',
    'complete',
    'blocked',
    'cancelled',
]);
// ----------------------------------------------------------------------------
// Punch item lifecycle (sub-records under a punch task)
// ----------------------------------------------------------------------------
export const PunchItemStatusSchema = z.enum(['open', 'assigned', 'in_progress', 'closed']);
// ----------------------------------------------------------------------------
// Attachment types (PM-provided documents + recap output)
// ----------------------------------------------------------------------------
export const AttachmentKindSchema = z.enum([
    'sales_order',
    'packing_slip',
    'design_package',
    'recap',
    'photo',
    'video',
    'note',
    'other',
]);
/** Page-level kind within a design package PDF. */
export const DesignPagePartSchema = z.enum([
    'cover',
    'as_built_furniture_plan',
    'as_built_3d_view',
    'as_built_furniture_schedule',
    'proposed_furniture_plan',
    'proposed_3d_view',
    'proposed_furniture_schedule',
    'general_notes',
    'other',
]);
// ----------------------------------------------------------------------------
// Notification channels and events (see ADR-0008)
// ----------------------------------------------------------------------------
export const NotificationChannelSchema = z.enum([
    'teams',
    'email',
    'sms',
    'in_app',
    'push',
]);
export const NotificationEventSchema = z.enum([
    'project_scheduled',
    'project_approval_requested',
    'project_approved',
    'crew_assignment_changed',
    'en_route',
    'on_site',
    // Crew started work on a task (in_progress) — PM + supervisors hear it kicked off.
    'task_started',
    // A crew marked a task/step complete — the PM + supervisors hear it's done
    // (distinct from complete_recap_available, which is the project-level closeout).
    'task_completed',
    // Crew finished a project they worked directly (D-3/A3: on_site/punch →
    // pending_closeout) — the PM + supervisors hear the field work is done and a
    // recap/closeout is due (the project analogue of task_completed). Debounced
    // like the other status notifications, so a Finish undone within the grace
    // window never fires. Distinct from complete_recap_available (the supervisor
    // then reviews the recap and closes the project out).
    'project_finished',
    'punch_walkthrough_ready',
    'complete_recap_available',
    'blocked_needs_attention',
    'note_added',
    // Approver requested a change on the public bid/budget approval page (§A6d) —
    // closes the loop back to the team instead of the request going silent.
    'approval_changes_requested',
    // A supervisor sent a PM's request back with changes (schedule/scope) before
    // scheduling it — the requestor needs to revise and resubmit (Calendar Phase 1).
    'project_changes_requested',
    // A scheduled project was moved to a different day/time (e.g. dragged on the
    // calendar) — the requestor + assigned crew are told, no re-approval needed
    // (Calendar Phase 2).
    'project_rescheduled',
    // A PM requested a task on an existing project from the calendar — supervisors
    // need to confirm it into committed work (Calendar Phase 4b).
    'task_requested',
    // A supervisor confirmed a PM-requested task — the requestor is told it's now
    // real scheduled work (Calendar Phase 4b).
    'task_confirmed',
    // Someone submitted an access request — admins (who approve) are notified
    // instead of having to poll the pending queue.
    'access_requested',
    // A scheduled task was cancelled or deleted — its assigned crew + the PM are
    // told so nobody shows up to removed work.
    'task_cancelled',
    // A blocked project was unblocked — the people who got the block alert hear
    // it's cleared.
    'blocked_resolved',
    // Fleet: a vehicle issue/defect was reported (→ ops), resolved (→ the
    // reporter), or a vehicle went out of service (→ ops; affects dispatch).
    'vehicle_issue_reported',
    'vehicle_issue_resolved',
    'vehicle_out_of_service',
    // Punch list: an item was flagged (→ ops + PM), assigned (→ the assignee), or
    // resolved/closed (→ ops + PM).
    'punch_item_added',
    // An existing punch item was edited or deleted (→ ops + PM).
    'punch_item_updated',
    'punch_item_assigned',
    'punch_item_resolved',
    // A project document (sales order / packing slip / design package / plans) was
    // added — the assigned crew + ops get the latest paperwork.
    'document_added',
    // Someone @mentioned a Vari user in a task's crew-only chat (demo #7) — only
    // the mentioned crew/supervisors are notified, never the PM/requester. Carries
    // a deep link to the task's chat.
    'crew_message_mention',
    // Reminders (Phase 2) — emitted by the daily reminder sweep, not a user action:
    'upcoming_job', // a scheduled job is N days out (→ crew + PM)
    'pickup_reminder', // a warehouse pickup is N days out (→ crew + PM)
    'registration_expiring', // a vehicle's registration/inspection is near due (→ ops)
    'daily_dispatch_summary', // the morning roll-up of today's jobs (→ ops)
    // A task's scheduled end is ~5 min away (demo #8) — its assigned crew get a
    // heads-up to mark it complete or extend. Global rule; fires once per
    // scheduled end, plus once per crew-requested extension. Never nags after.
    'task_ending_soon',
    // A directly-worked PROJECT (D-3/A3/E) is ~10 min from its committed start /
    // end — the assigned crew (and Leo) get a heads-up to Start / Finish it. Fires
    // once per project per committed day; links to the project where the button is.
    'project_starting_soon',
    'project_ending_soon',
    // A PICKUP (workstream C) is ~10 min from its scheduled start — the assigned
    // crew get a heads-up to head out. Fires once per pickup per day; links to the
    // crew pickup screen.
    'pickup_starting_soon',
    // Approval reminders (Phase 2b) — re-nudge while an approval sits unactioned:
    'request_approval_reminder', // incoming request pending supervisor approval (→ supervisors)
    'bid_approval_reminder', // sent bid/date approval pending sign-off (→ requestor/PM)
]);
// ----------------------------------------------------------------------------
// Locations (Vari warehouses, VariSpace tenants, supplier sites, client sites)
// ----------------------------------------------------------------------------
export const LocationKindSchema = z.enum([
    'vari_warehouse',
    'vari_space_tenant',
    'client_site',
    'supplier',
    'other',
]);
// ----------------------------------------------------------------------------
// Project source / intake path
// ----------------------------------------------------------------------------
export const ProjectSourceSchema = z.enum([
    'form',
    'ai_chat',
    'pdf_upload',
    'email_forward',
    'supervisor_manual',
]);
// ----------------------------------------------------------------------------
// Project kind — the category of field work. A behavioral tag, NOT a
// structural fork: all kinds share one Project entity + lifecycle. Drives
// task-proposal defaults, intake field visibility, soft validation, and
// leadership reporting. Fields a given kind doesn't use simply stay null.
// ----------------------------------------------------------------------------
export const ProjectKindSchema = z.enum([
    'install', // delivery + install — the full-metadata default
    'reconfigure', // move / rework existing furniture
    'punch', // fix-list / warranty visit (often follows a prior job)
    'service', // ad-hoc labor / service call
    'pickup', // RMA / removal / haul-off
    'other',
]);
// ----------------------------------------------------------------------------
// Priority
// ----------------------------------------------------------------------------
export const PrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);
// ----------------------------------------------------------------------------
// Vehicle (fleet) type — trucks are a shared pool, assigned per task.
// ----------------------------------------------------------------------------
export const VehicleTypeSchema = z.enum([
    'cargo_van',
    'box_truck',
    'pickup',
    'trailer',
    'other',
]);
/** Fleet availability — 'active' is bookable, 'out_of_service' is temporarily
 *  down (in the shop), 'retired' is gone from the fleet. */
export const VehicleStatusSchema = z.enum(['active', 'out_of_service', 'retired']);
/** Service-log entry categories. */
export const VehicleServiceCategorySchema = z.enum([
    'oil_change',
    'tires',
    'brakes',
    'inspection',
    'repair',
    'registration',
    'other',
]);
/** Vehicle issue severity + lifecycle. */
export const VehicleIssueSeveritySchema = z.enum(['low', 'normal', 'high']);
export const VehicleIssueStatusSchema = z.enum(['open', 'resolved']);
//# sourceMappingURL=enums.js.map